import _                                                              from 'underscore';
import moment                                                         from 'moment';
import { v4 as uuidv4 }                                               from 'uuid'
import { v5 as uuidv5 }                                               from 'uuid'
import { parse as uuidParse }                                         from 'uuid';
import React, { useState, useCallback }                               from 'react';
import { CORVAL_PRODUCT_NAME, ENVIRONMENT_IS_DEVELOPMENT, LOG_TYPES } from '../constants/SystemEnums';
import { useCurrentUser }                                             from './useAccount';
import { Uuid }                                                       from '../types';

const PRODUCT_NAMESPACE = CORVAL_PRODUCT_NAME,
      LOGGING_LAYER     = "ClientJS";

interface LoggingMessageInfo {
	type: LOG_TYPES,
	
	[ i: string ]: any,
}

interface LogRecord {
	type: LoggingMessageInfo['type'],
	message: string,
	_desc: Omit<LoggingMessageInfo, 'type'>,
	_meta: {
		// Unix timestamp
		timestamp: number,
		
		// string identifier of where the log in the platform occurred, window.location
		location: string,
		
		// product identifier
		product: string,
		
		// layer identifier
		layer: string,
		
		// user identifier, not their id
		user: string,
		
		// DEPRECATED: user Uuid in the particular env / system
		userId?: Uuid,
		
		// DEPRECATED: user name or other id in the particular env / system
		userName?: string,
	}
}

type LogMethod<T> = ( message: string, info?: Partial<LoggingMessageInfo> ) => T;

type RawLogMethod<T> = ( record: LogRecord ) => T;

type UseLoggingHook = ( storageKey?: string ) => UseLoggingHookActions;

export interface UseLoggingHookActions {
	
	// Gets the current loggers storage key
	currentKey(): string,
	
	// Set the current loggers storage key
	setCurrentKey( storageKey: string ): void,
	
	// Clears the user's session log
	clearSessionLog(): void,
	
	// Usage logger method
	usage: LogMethod<void>,
	
	// Diagnostic logger method
	diagnostic: LogMethod<void>,
	
	// Error logger method
	error: LogMethod<void>,
	
	// Starts performance logger, returns a session key for ID later when stopping
	perfLoggerStart: LogMethod<string>,
	
	// Stops performance logger, must be given a session key to ID on
	perfLoggerStop( sessionKey: string, consoleOutput?: boolean, capturePerfRecord?: boolean ): void
}

export const createLoggingSessionStorageKey = ( entryKey: string, prefix = 'log' ) => {
	
	let defaultEntryKey = `${ ( new Date() ).getTime() }--${ uuidv4() }`;
	
	return ( `${ prefix }--${ entryKey || defaultEntryKey }` );
};

export const useLogging: UseLoggingHook = ( storageKey = null ) => {
	
	let { currentUser } = useCurrentUser(),
	    userId          = _.property( 'id' )( currentUser ),
	    hashedUserId    = null;
	
	// obscure user id
	if( userId ){
		hashedUserId = uuidv5( userId, uuidParse( userId ) );
	}
	
	// store key as hashedUserId or given storageKey
	let [ sessionStorageKey, setKey ] = useState( createLoggingSessionStorageKey( hashedUserId || storageKey ) ),
	    [ userHashId, setUserHashId ] = useState( hashedUserId );
	
	const getJsLoggingInfo: LogMethod<LogRecord> = ( message, info ) => {
		
		let { type, ...additionalInfo } = info;
		
		return {
			type:    additionalInfo.type,
			message: message,
			_desc:   { ...additionalInfo, },
			_meta:   {
				timestamp: moment().valueOf(),
				location:  window.location.toString(),
				product:   PRODUCT_NAMESPACE,
				layer:     LOGGING_LAYER,
				user:      userHashId
				// Not sure if we need user info for logging. TBD.
				// userId:    _.property( 'id' )( currentUser ) || null,
				// userName:  _.property( 'emailAddress' )( currentUser ) || null,
			}
		};
	};
	
	const log: LogMethod<void> = useCallback( ( message, additionalInfo = { type: LOG_TYPES.DEFAULT } ) => {
		
		let newEntry   = getJsLoggingInfo( message, additionalInfo ),
		    logEntries = sessionStorage[ sessionStorageKey ];
		
		if( logEntries ){
			
			logEntries = JSON.parse( sessionStorage[ sessionStorageKey ] );
			logEntries.push( newEntry );
			
		} else {
			
			logEntries = [ newEntry ];
		}
		
		sessionStorage[ sessionStorageKey ] = JSON.stringify( logEntries );
		
	}, [ sessionStorageKey ] );
	
	const logData: RawLogMethod<void> = useCallback( ( record ) => {
		
		let logEntries = sessionStorage[ sessionStorageKey ];
		
		if( logEntries ){
			
			logEntries = JSON.parse( sessionStorage[ sessionStorageKey ] );
			logEntries.push( record );
			
		} else {
			
			logEntries = [ record ];
		}
		
		sessionStorage[ sessionStorageKey ] = JSON.stringify( logEntries );
		
	}, [ sessionStorageKey ] );
	
	return {
		
		currentKey: () => sessionStorageKey,
		
		setCurrentKey: ( storageKey ) => {
			
			let entries = sessionStorage[ sessionStorageKey ],
			    newKey  = createLoggingSessionStorageKey( storageKey );
			
			setKey( newKey );
			
			sessionStorage[ newKey ] = entries;
		},
		
		clearSessionLog: () => {
			
			sessionStorage.removeItem( sessionStorageKey );
		},
		
		usage: ( message, additionalInfo = {} ) => {
			
			log( message, { ...additionalInfo, type: LOG_TYPES.USAGE } );
			
		},
		
		diagnostic: ( message, additionalInfo = {} ) => {
			
			log( message, { ...additionalInfo, type: LOG_TYPES.DIAGNOSTIC } );
			
		},
		
		error: ( message, additionalInfo = {} ) => {
			
			log( message, { ...additionalInfo, type: LOG_TYPES.ERROR } );
			
		},
		
		perfLoggerStart: ( message, additionalInfo = {} ) => {
			
			// create a key. btoa just does base64 encoding
			let sessionKey = btoa( message );
			
			// set the info, then return the key for removal
			sessionStorage[ sessionKey ] = JSON.stringify( getJsLoggingInfo( message, additionalInfo ) );
			
			return sessionKey;
		},
		
		perfLoggerStop: ( sessionKey, consoleOutput = true, capturePerfRecord = false ) => {
			
			let now      = moment().valueOf(),
			    logEntry = JSON.parse( sessionStorage[ sessionKey ] ) as LogRecord;
			
			// get the duration
			logEntry._desc.elapsedMS = now - logEntry._meta.timestamp;
			
			// capture output?
			if( capturePerfRecord ){
				logData( logEntry );
			}
			
			if( consoleOutput && ENVIRONMENT_IS_DEVELOPMENT ){
				console.log( logEntry );
			}
			
			// remove from sessions storage
			sessionStorage.removeItem( sessionKey );
			
		}
	}
};
